import axios from "axios";
import host from "../global";
import { useSelector } from "react-redux";
export const getInterviewCandidatesDataService = async (
  page = 1,
  limit = 10,
  job_position = "All",
  combined_status = "All",
  min_score = 0,
  max_score = 100
) => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");
    const response = await axios.get(`${host}/get_candidates_data`, {
      params: {
        page,
        limit,
        job_position,
        combined_status,
        min_score,
        max_score,
      },
      headers:{
        Authorization: `Bearer ${accessToken}`,
      }
    });

    const {candidates , current_user_id ,total_count } = response.data;

    if(!candidates || !Array.isArray(candidates)){
        throw new Error("Invalid data format received from server");
    }
    return response.data;
  } catch (error) {
    console.error("getInterviewCandidatesDataService ERROR:", error);
    return null;
  }
};


export const updateCandidateStatusService = async ({
  interview_id,
  status,
  reason = "",
  candidate_name = "",
  candidate_email = "",
  candidate_number = "",
  hr_owner = "",
  date_of_interview = "",
  interview_score = "NA",
  job_role = ""
}) => {
  try {
    if (!interview_id || !status) {
      throw new Error("interview_id and status are required");
    }

    const accessToken = sessionStorage.getItem("accessToken");
    if (!accessToken) {
      throw new Error("Access token missing");
    }

    const response = await axios.post(
      `${host}/interview/update_decision/${interview_id}`,
      {
        status,
        reason,
        candidate_name,
        candidate_email,
        candidate_number,
        hr_owner,
        date_of_interview,
        interview_score,
        job_role
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "❌ Error while updating candidate status:",
      error?.response?.data || error.message
    );
    return null;
  }
};


export const interviewStatusMap = {
  SCHEDULED: {
    combined_status: "Pending",
    filterby: "SCHEDULED",
  },
  PROGRESS: {
    combined_status: "In Progress",
    filterby: "PROGRESS",
  },
  COMPLETED: {
    combined_status: "Completed",
    filterby: "COMPLETED",
  },
};

export const buildInterviewThunkParams = ({
  filterState,
  activeSubTab,
  page = 1,
  limit = 10,
  org_id = "",
  isInvitedDateVisible = true,
}) => {
  const {
    searchQuery,
    dateRange,
    currentJobRole,
    currentOwner,
    currentScoreRange,
    scheduledStatusFilter
  } = filterState;

  //  ---------------- Scheduled Tab Status Filter -------------
  const scheduledStatusFilterForAPI = scheduledStatusFilter === "" ? "ALL":scheduledStatusFilter.toUpperCase();
  // ---------------- Status Mapping ----------------
  const statusConfig = interviewStatusMap[activeSubTab] || {
    combined_status: "All",
    filterby: "SCHEDULED",
  };

  // ---------------- Date Range ----------------
  // Single Date Range filter serves both invited_date and end_date:
  // - Invited Date column visible  -> filter applies to invited_date (and end_date)
  // - Invited Date column hidden   -> filter applies to end_date only
  let start_date = "";
  let end_date = "";
  let interview_start_filter = "";
  let interview_end_filter = "";

  if (Array.isArray(dateRange) && dateRange.length === 2) {
    const [start, end] = dateRange;
    if (start && end) {
      if (isInvitedDateVisible) {
        start_date = start;
        end_date = end;
      }
      interview_start_filter = start;
      interview_end_filter = end;
    }
  }

  // ---------------- Score Range ----------------
  let min_score = 0;
  let max_score = 100;

  if (currentScoreRange) {
    const { min, max } = currentScoreRange; 
    min_score = min ?? 0;
    max_score = max ?? 100;
  }

  return {
    orgId:org_id,
    page,
    limit,
    search_query: searchQuery,
    combined_status: statusConfig.combined_status,
    filterby: statusConfig.filterby,
    job_position: currentJobRole || "All",
    owner: currentOwner || "All",
    min_score,
    max_score,
    start_date,
    end_date,
    interview_start_filter,
    interview_end_filter,
    scheduleFilter:scheduledStatusFilterForAPI,
  };
};



// Downloads a .zip containing a single folder with the resumes of the given interviews.
// Throws an Error whose message is safe to show to the user.
export const downloadResumesZipService = (interviewIds) => {
  if (!interviewIds?.length) {
    throw new Error("Select at least one candidate to download resumes.");
  }
  return postForResumesZip("/bulk-download-resumes", { interview_ids: interviewIds });
};

// Screened Candidates rows: [{source: "MY_UPLOADS", upload_id, unique_id} | {source: "SOURCED", id}]
export const downloadScreenedResumesZipService = (candidates) => {
  if (!candidates?.length) {
    throw new Error("Select at least one candidate to download resumes.");
  }
  return postForResumesZip("/bulk-download-screened-resumes", { candidates });
};

const postForResumesZip = async (path, body) => {
  const accessToken = sessionStorage.getItem("accessToken");
  try {
    const response = await axios.post(
      `${host}${path}`,
      body,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        responseType: "blob",
      }
    );

    const disposition = response.headers["content-disposition"] || "";
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    const filename =
      match?.[1] ||
      `Interview_Resumes_${new Date().toISOString().split("T")[0]}.zip`;

    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return filename;
  } catch (error) {
    // With responseType "blob" the JSON error body arrives as a Blob.
    let message = "Failed to download resumes";
    try {
      const body = error?.response?.data;
      const parsed = body instanceof Blob ? JSON.parse(await body.text()) : body;
      if (typeof parsed?.detail === "string") message = parsed.detail;
    } catch {
      // keep the generic message
    }
    throw new Error(message);
  }
};

export const deleteInterviewCandidateDataService = async (interviewId) => {
  try {
     const accessToken = sessionStorage.getItem("accessToken");
    const response = await axios.delete(
      `${host}/deletecandidate_data`,
      {
        params: { interview_id: interviewId },
        headers:{
          Authorization:`Bearer ${accessToken}`
        }
      }
    );

    return Boolean(response.data);
  } catch (error) {
    console.error("Delete candidate failed:", error);
    return false;
  }
};




export const reSchedulecandidateInterviewService = async (
  interviewId,
  formattedDateForEmail,
  timeFrameStartDate = null,
  timeFrameEndDate = null
) => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");
 
    const requestBody = {};
    if (timeFrameStartDate && timeFrameEndDate) {
      requestBody.start_date = timeFrameStartDate;
      requestBody.end_date = timeFrameEndDate;
    }
 
    const response = await axios.post(
      `${host}/candidate/schedule_test/${interviewId}/${formattedDateForEmail}`,
      requestBody,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
 
    return Boolean(response.data);
  } catch (error) {
    console.error("Rescheduling candidate failed:", error);
    return false;
  }
};
 